/**
 * Collapsed form sections stay mounted (rule 3b.2): their fields keep their values, validators and errors, and a
 * programmatic focus — the validation gate, a prefill, a leg's remedy link, the field a failed test is about — first
 * opens every collapsed section around the control. `revealControl` asks the sections through a bubbling DOM event, so
 * any caller (a hook, a pure focus helper) can do it without holding the sections' state.
 *
 * C2 review C2-04: the section opens through an ordinary state update and runs `then` (the focus or the scroll) only
 * after the opening has COMMITTED (`useRevealOnRequest`'s layout effect). A synchronous flush from inside the event is
 * not possible when the reveal comes from an effect — the scroll used to hit a still-hidden field. With every section
 * around the control already open, `then` runs at once.
 */
import { useEffect, useLayoutEffect, useRef, type RefObject } from 'react';

export const REVEAL_EVENT = 'connection-form:reveal';

/** What a closed section takes from a reveal: a release it calls once its opening has committed. */
export type RevealRequest = { hold: () => () => void };

export function revealControl(element: Element | null | undefined, then?: () => void): void {
  if (!element) return;
  let waiting = 0;
  let dispatched = false;
  const release = () => {
    waiting -= 1;
    if (dispatched && waiting === 0) then?.();
  };
  const request: RevealRequest = { hold: () => { waiting += 1; return release; } };
  element.dispatchEvent(new CustomEvent<RevealRequest>(REVEAL_EVENT, { bubbles: true, detail: request }));
  dispatched = true;
  if (waiting === 0) then?.();
}

/** Reveal, then scroll the control into view (never moves focus): the field a failed test is about. */
export function revealAndScroll(selector: string, root: ParentNode = document): void {
  const element = root.querySelector<HTMLElement>(selector);
  if (!element) return;
  revealControl(element, () => element.scrollIntoView?.({ block: 'nearest' }));
}

/**
 * Makes a collapsible container answer reveal requests: while closed it opens (`openIt`) and holds each request until
 * the opening committed. Used by `FormSection` and the storage endpoints disclosure.
 */
export function useRevealOnRequest(ref: RefObject<HTMLElement | null>, open: boolean, openIt: () => void): void {
  const openRef = useRef(open);
  openRef.current = open;
  const openItRef = useRef(openIt);
  openItRef.current = openIt;
  const held = useRef<Array<() => void>>([]);

  useEffect(() => {
    const container = ref.current;
    if (!container) return undefined;
    const onReveal = (event: Event) => {
      if (openRef.current) return;
      held.current.push((event as CustomEvent<RevealRequest>).detail.hold());
      openItRef.current();
    };
    container.addEventListener(REVEAL_EVENT, onReveal);
    return () => container.removeEventListener(REVEAL_EVENT, onReveal);
  }, [ref]);

  useLayoutEffect(() => {
    if (!open || held.current.length === 0) return;
    const releases = held.current;
    held.current = [];
    for (const release of releases) release();
  }, [open]);
}
